import { RingBuffer } from "./ring-buffer";
import { serializeArgs } from "./serialize";
import type { ConsoleEntry, ConsoleLevel } from "./types";

const CAPACITY = 100;

const LEVELS: ConsoleLevel[] = ["log", "info", "warn", "error", "debug"];

export interface ConsoleCapture {
  snapshot(): ConsoleEntry[];
  count(): number;
  stop(): void;
}

let active: ConsoleCapture | null = null;

/**
 * Patche console.* et écoute les erreurs globales. Idempotent : un second
 * appel renvoie l'instance déjà active (ex. double montage en StrictMode).
 */
export function startConsoleCapture(): ConsoleCapture {
  if (active) return active;

  const buffer = new RingBuffer<ConsoleEntry>(CAPACITY);
  const originals = new Map<ConsoleLevel, (...args: unknown[]) => void>();

  for (const level of LEVELS) {
    const original = console[level].bind(console);
    originals.set(level, original);
    console[level] = (...args: unknown[]) => {
      try {
        buffer.push({
          level,
          message: serializeArgs(args),
          timestamp: new Date().toISOString(),
          ...(level === "error" && args[0] instanceof Error && args[0].stack
            ? { stack: args[0].stack.slice(0, 1500) }
            : {}),
        });
      } catch {
        // La capture ne doit jamais casser la console de l'hôte.
      }
      original(...args);
    };
  }

  const onError = (event: ErrorEvent) => {
    buffer.push({
      level: "error",
      message: serializeArgs([event.message]),
      timestamp: new Date().toISOString(),
      ...(event.error instanceof Error && event.error.stack
        ? { stack: event.error.stack.slice(0, 1500) }
        : {}),
    });
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    buffer.push({
      level: "error",
      message: "Promesse rejetée non gérée : " + serializeArgs([reason]),
      timestamp: new Date().toISOString(),
      ...(reason instanceof Error && reason.stack ? { stack: reason.stack.slice(0, 1500) } : {}),
    });
  };

  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);

  active = {
    snapshot: () => buffer.snapshot(),
    count: () => buffer.size,
    stop() {
      for (const [level, original] of originals) console[level] = original;
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      active = null;
    },
  };
  return active;
}
